import { supabase } from './supabase';

/**
 * Publiczne identyfikatory aplikacji platform spolecznosciowych.
 *
 * Sekretow tu nie ma i byc nie moze: Vite wstrzykuje kazda zmienna z prefiksem VITE_
 * w kod przegladarki na etapie builda, wiec wszystko, co tu wejdzie z
 * `import.meta.env`, widzi kazdy odwiedzajacy strone. Operacje wymagajace sekretu
 * robia funkcje brzegowe - dla Meta `meta-token-exchange` (patrz `src/lib/metaTokens.ts`),
 * dla pozostalych platform ich wlasne callbacki OAuth.
 *
 * Klucze samych uzytkownikow z `user_settings` to osobna warstwa: trafiaja wylacznie
 * do przegladarki wlasciciela i sa edytowane w `ApiSettings`.
 */
export interface ApiCredentials {
  facebook_app_id: string;
  instagram_app_id: string;
  linkedin_client_id: string;
  linkedin_social_client_id: string;
  youtube_client_id: string;
  google_business_client_id: string;
  tiktok_client_key: string;
  tiktok_business_client_id: string;
  x_api_key: string;
  pinterest_app_id: string;
  pinterest_access_tier: 'trial' | 'standard';
}

export async function getUserApiCredentials(): Promise<ApiCredentials> {
  const defaultCredentials: ApiCredentials = {
    facebook_app_id: import.meta.env.VITE_FACEBOOK_APP_ID || '',
    instagram_app_id: import.meta.env.VITE_INSTAGRAM_APP_ID || import.meta.env.VITE_FACEBOOK_APP_ID || '',
    linkedin_client_id: import.meta.env.VITE_LINKEDIN_CLIENT_ID || '',
    linkedin_social_client_id: import.meta.env.VITE_LINKEDIN_SOCIAL_CLIENT_ID || '',
    youtube_client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    google_business_client_id: import.meta.env.VITE_GOOGLE_BUSINESS_CLIENT_ID || '',
    tiktok_client_key: import.meta.env.VITE_TIKTOK_CLIENT_KEY || '',
    tiktok_business_client_id: import.meta.env.VITE_TIKTOK_BUSINESS_APP_ID || '',
    x_api_key: import.meta.env.VITE_X_API_KEY || '',
    pinterest_app_id: import.meta.env.VITE_PINTEREST_APP_ID || '',
    pinterest_access_tier: 'trial',
  };

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('🔑 No user logged in, using default credentials');
      return defaultCredentials;
    }

    console.log('🔑 Fetching credentials for user:', user.id);
    const { data, error } = await supabase
      .from('user_settings')
      .select('facebook_app_id, instagram_app_id, linkedin_client_id, linkedin_social_client_id, youtube_client_id, google_business_client_id, tiktok_client_key, tiktok_business_client_id, x_api_key, pinterest_app_id, pinterest_access_tier')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('🔑 Error fetching user credentials:', error);
      return defaultCredentials;
    }

    if (!data) {
      console.log('🔑 No user_settings found, using default credentials');
      return defaultCredentials;
    }

    console.log('🔑 User credentials loaded - Facebook App ID:', data.facebook_app_id || 'not set');

    return {
      facebook_app_id: data.facebook_app_id ?? defaultCredentials.facebook_app_id,
      instagram_app_id: data.instagram_app_id ?? defaultCredentials.instagram_app_id,
      linkedin_client_id: data.linkedin_client_id ?? defaultCredentials.linkedin_client_id,
      linkedin_social_client_id: data.linkedin_social_client_id ?? defaultCredentials.linkedin_social_client_id,
      youtube_client_id: data.youtube_client_id ?? defaultCredentials.youtube_client_id,
      google_business_client_id: data.google_business_client_id ?? defaultCredentials.google_business_client_id,
      tiktok_client_key: data.tiktok_client_key ?? defaultCredentials.tiktok_client_key,
      tiktok_business_client_id: data.tiktok_business_client_id ?? defaultCredentials.tiktok_business_client_id,
      x_api_key: data.x_api_key ?? defaultCredentials.x_api_key,
      pinterest_app_id: data.pinterest_app_id ?? defaultCredentials.pinterest_app_id,
      pinterest_access_tier: data.pinterest_access_tier === 'standard' ? 'standard' : 'trial',
    };
  } catch (error) {
    console.error('Error loading user API credentials:', error);
    return defaultCredentials;
  }
}

export async function getFacebookCredentials(): Promise<{ appId: string }> {
  const credentials = await getUserApiCredentials();
  return { appId: credentials.facebook_app_id };
}

export async function getInstagramCredentials(): Promise<{ appId: string }> {
  const credentials = await getUserApiCredentials();
  return { appId: credentials.instagram_app_id };
}

export async function getInstagramDirectCredentials(): Promise<{ appId: string }> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return { appId: '' };
  }

  // Filtr po sekrecie, a nie jego odczyt: `meta-token-exchange` uzna aplikacje Direct
  // tylko wtedy, gdy ma i id, i sekret, wiec front musi rozstrzygac to tak samo,
  // inaczej SDK dostalby id jednej aplikacji, a serwer sekret drugiej.
  const { data } = await supabase
    .from('user_settings')
    .select('instagram_direct_app_id')
    .eq('user_id', user.id)
    .not('instagram_direct_app_id', 'is', null)
    .not('instagram_direct_app_secret', 'is', null)
    .maybeSingle();

  if (data?.instagram_direct_app_id) {
    return { appId: data.instagram_direct_app_id };
  }

  // Fallback to regular Instagram credentials if Direct not configured
  return getInstagramCredentials();
}
